import React, { useMemo, useState } from 'react';
import CreditIcon from '../CreditIcon';

const normalizeGender = (gender) => {
  const normalized = (gender ?? '').toString().trim().toLowerCase();

  if (['uomo', 'male', 'm', 'man'].includes(normalized)) return 'Uomo';
  if (['donna', 'female', 'f', 'woman'].includes(normalized)) return 'Donna';

  return (gender ?? '').toString().trim();
};

const getMinimumCount = (value) => {
  const minimum = Number(value ?? 0);
  return Number.isFinite(minimum) ? Math.max(0, minimum) : 0;
};

const countMembersByGender = (members = []) =>
  members.reduce(
    (counts, member) => {
      const gender = normalizeGender(member?.gender);

      if (gender === 'Uomo') counts.Uomo += 1;
      if (gender === 'Donna') counts.Donna += 1;

      return counts;
    },
    { Uomo: 0, Donna: 0 }
  );

const getGenderRequirementText = (count, genderKey) => {
  if (!count) return '';

  if (genderKey === 'Uomo') {
    return `${count} ${count === 1 ? 'uomo' : 'uomini'}`;
  }

  return `${count} ${count === 1 ? 'donna' : 'donne'}`;
};

const buildGenderStateMessage = (state) => {
  const missingItems = [];

  if (state.missingFemale > 0) missingItems.push(getGenderRequirementText(state.missingFemale, 'Donna'));
  if (state.missingMale > 0) missingItems.push(getGenderRequirementText(state.missingMale, 'Uomo'));

  if (missingItems.length === 0) {
    return '';
  }

  if (state.impossible) {
    return `Non puoi selezionare, devi avere almeno ${missingItems.join(' e ')}.`;
  }

  return `Per chiudere lo scambio devi ancora inserire ${missingItems.join(' e ')}.`;
};

const renderSwapBanner = (swapsBlockedAt, autoBlocked = false) => {
  const color = autoBlocked ? '#f0ad4e' : '#0dcaf0';

  return (
    <div
      className="d-flex align-items-start gap-3 rounded-4 border mb-4 p-3"
      style={{
        backgroundColor: `${color}10`,
        borderColor: color,
        color: 'rgba(255,255,255,0.8)',
      }}
    >
      <i
        className={`bi ${autoBlocked ? 'bi-clock-fill' : 'bi-info-circle-fill'} fs-4 mt-1`}
        style={{ color }}
      ></i>
      <div>
        <h5 className="mb-1 fw-bold text-white">
          {autoBlocked ? 'Scambi Temporaneamente Bloccati!' : 'Scambi Bloccati!'}
        </h5>
        <div className="mb-0 small">
          {autoBlocked
            ? 'Gli scambi sono temporaneamente bloccati durante gli show in corso. Tornano disponibili alle 12:00 del giorno successivo allo show.'
            : 'Non e possibile scambiare wrestler prima della fine del prossimo PLE'}
          {!autoBlocked && swapsBlockedAt && (
            <small className="d-block mt-1" style={{ opacity: 0.7 }}>
              Blocco attivo dal: {new Date(swapsBlockedAt).toLocaleString('it-IT')}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

function EditFactionFormWrestler({
  faction,
  roster,
  members,
  allWrestlers,
  favoriteWrestlerIds = [],
  selectedToRemove,
  selectedToAdd,
  swapCount,
  maxSwaps,
  isWithinFirstDay,
  freeSwapEndDate,
  canEditMembers,
  autoBlocked,
  swapBlockData,
  priceDetails,
  remainingBudget,
  currentShow,
  onToggleRemove,
  onToggleAdd,
  onSaveMembers,
  isSaving,
  error,
}) {
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [brandFilter, setBrandFilter] = useState('');
  const [genderFilter, setGenderFilter] = useState('');
  const [wrestlerSearch, setWrestlerSearch] = useState('');
  const minMaleWrestlers = getMinimumCount(roster?.minMaleWrestlers);
  const minFemaleWrestlers = getMinimumCount(roster?.minFemaleWrestlers);
  const hasGenderRequirements = minMaleWrestlers > 0 || minFemaleWrestlers > 0;

  const currentMemberIds = useMemo(
    () => new Set(members.map((member) => member.id)),
    [members]
  );
  const availableBrands = useMemo(
    () =>
      [...new Set(allWrestlers.map((wrestler) => wrestler.brand).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'it')
      ),
    [allWrestlers]
  );
  const availableGenders = useMemo(
    () =>
      [...new Set(allWrestlers.map((wrestler) => wrestler.gender).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b, 'it')
      ),
    [allWrestlers]
  );
  const allWrestlersById = useMemo(
    () =>
      allWrestlers.reduce((acc, wrestler) => {
        acc[wrestler.id] = wrestler;
        return acc;
      }, {}),
    [allWrestlers]
  );
  const selectedToRemoveCredits = useMemo(
    () =>
      selectedToRemove.reduce((sum, id) => {
        const member = members.find((item) => item.id === id);
        return sum + (member?.cost || 0);
      }, 0),
    [members, selectedToRemove]
  );
  const selectedToAddCost = useMemo(
    () =>
      selectedToAdd.reduce((sum, id) => sum + (allWrestlersById[id]?.price || 0), 0),
    [allWrestlersById, selectedToAdd]
  );
  const hasActiveFilters = Boolean(wrestlerSearch || brandFilter || genderFilter);
  const genderSelectionState = useMemo(() => {
    const removedIds = new Set(selectedToRemove);
    const keptMembers = members.filter((member) => !removedIds.has(member.id));
    const incomingMembers = selectedToAdd
      .map((id) => allWrestlersById[id])
      .filter(Boolean);
    const projectedMembers = [...keptMembers, ...incomingMembers];
    const counts = countMembersByGender(projectedMembers);
    const missingMale = Math.max(0, minMaleWrestlers - counts.Uomo);
    const missingFemale = Math.max(0, minFemaleWrestlers - counts.Donna);
    const remainingSlots = Math.max(0, selectedToRemove.length - selectedToAdd.length);
    const totalMissing = missingMale + missingFemale;
    const impossible = totalMissing > remainingSlots;

    return {
      counts,
      missingMale,
      missingFemale,
      remainingSlots,
      impossible,
      message: buildGenderStateMessage({
        missingMale,
        missingFemale,
        impossible,
      }),
    };
  }, [
    allWrestlersById,
    members,
    minFemaleWrestlers,
    minMaleWrestlers,
    selectedToAdd,
    selectedToRemove,
  ]);

  const BrandBadge = ({ brand }) => {
    const brandClass =
      brand === 'Raw'
        ? 'bg-raw'
        : brand === 'SmackDown'
          ? 'bg-sd'
          : brand === 'NXT'
            ? 'bg-nxt'
            : 'bg-dark';

    return <span className={`badge ${brandClass} px-2 py-1`}>{brand || 'FA'}</span>;
  };

  const getStatusMeta = (status) => {
    if (status === 'Main_Eventer') {
      return {
        label: 'Main Eventer',
        className: 'bg-warning bg-opacity-25 text-warning',
      };
    }

    if (status === 'Backstage') {
      return {
        label: 'Backstage',
        className: 'bg-secondary bg-opacity-25 text-light',
      };
    }

    return {
      className: 'bg-brand-secondary bg-opacity-25 text-brand-secondary',
    };
  };

  const handleSelectableRowKeyDown = (event, callback, disabled = false) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      callback();
    }
  };

  const renderSelectionAvatar = (item, isSelected) => (
    <div
      className={`edit-faction-market__selection-avatar ${isSelected ? 'is-selected' : ''}`}
      aria-hidden="true"
    >
      {item.imageUrl ? (
        <img src={item.imageUrl} alt="" className="edit-faction-market__avatar" />
      ) : (
        <div className="edit-faction-market__avatar edit-faction-market__avatar--placeholder">
          <i className="bi bi-person-fill"></i>
        </div>
      )}
      <span className={`edit-faction-market__selection-check ${isSelected ? 'is-visible' : ''}`}>
        <i className="bi bi-check-lg"></i>
      </span>
    </div>
  );

  const filteredWrestlers = useMemo(
    () =>
      allWrestlers
        .filter((wrestler) => {
          if (currentMemberIds.has(wrestler.id)) return false;
          if (wrestlerSearch && !wrestler.name.toLowerCase().includes(wrestlerSearch.toLowerCase())) return false;
          if (brandFilter && wrestler.brand !== brandFilter) return false;
          if (genderFilter && wrestler.gender !== genderFilter) return false;
          return true;
        })
        .sort((a, b) => {
          const aFavorite = favoriteWrestlerIds.includes(a.id);
          const bFavorite = favoriteWrestlerIds.includes(b.id);

          if (aFavorite !== bFavorite) {
            return aFavorite ? -1 : 1;
          }

          let comparison = 0;
          if (sortField === 'name') comparison = a.name.localeCompare(b.name, 'it');
          else if (sortField === 'score') comparison = a.score - b.score;
          else if (sortField === 'price') comparison = a.price - b.price;
          return sortDirection === 'asc' ? comparison : -comparison;
        }),
    [
      allWrestlers,
      brandFilter,
      currentMemberIds,
      favoriteWrestlerIds,
      genderFilter,
      sortDirection,
      sortField,
      wrestlerSearch
    ]
  );
  const getCandidateGenderState = (wrestlerId) => {
    if (!hasGenderRequirements) {
      return { impossible: false, message: '' };
    }

    const candidateSelectedIds = selectedToAdd.includes(wrestlerId)
      ? selectedToAdd
      : [...selectedToAdd, wrestlerId];
    const removedIds = new Set(selectedToRemove);
    const keptMembers = members.filter((member) => !removedIds.has(member.id));
    const incomingMembers = candidateSelectedIds
      .map((id) => allWrestlersById[id])
      .filter(Boolean);
    const projectedMembers = [...keptMembers, ...incomingMembers];
    const counts = countMembersByGender(projectedMembers);
    const missingMale = Math.max(0, minMaleWrestlers - counts.Uomo);
    const missingFemale = Math.max(0, minFemaleWrestlers - counts.Donna);
    const remainingSlots = Math.max(0, selectedToRemove.length - candidateSelectedIds.length);
    const totalMissing = missingMale + missingFemale;
    const impossible = totalMissing > remainingSlots;

    return {
      missingMale,
      missingFemale,
      remainingSlots,
      impossible,
      message: buildGenderStateMessage({
        missingMale,
        missingFemale,
        impossible,
      }),
    };
  };

  const blockInfo =
    autoBlocked || (swapBlockData && !swapBlockData.enabled)
      ? { show: true, autoBlocked }
      : { show: false };

  return (
    <div className="tab-content edit-faction-market">
      {blockInfo.show && renderSwapBanner(swapBlockData?.blockedAt, autoBlocked)}

      {!canEditMembers ? (
        <div className="alert alert-warning d-flex align-items-start gap-3 rounded-4 border-0 shadow-sm mb-4 bg-warning bg-opacity-20 text-light">
          <i className="bi bi-exclamation-triangle-fill fs-4 mt-1 text-brand-secondary"></i>
          <div>
            <h5 className="mb-2 brand-title">Modifica membri non disponibile</h5>
            <p className="mb-0">
              {faction?.roster === 'Road to WrestleMania'
                ? 'La modifica dei membri non e disponibile per le fazioni Road to WrestleMania'
                : 'La modifica dei membri e consentita solo per roster Indipendenti con scambi disponibili.'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {isWithinFirstDay && freeSwapEndDate && (
            <div
              className="d-flex align-items-start gap-3 rounded-4 border mb-4 p-3"
              style={{
                backgroundColor: '#28a74510',
                borderColor: '#28a745',
                color: 'rgba(255,255,255,0.8)',
              }}
            >
              <i className="bi bi-arrow-repeat fs-4 mt-1" style={{ color: '#28a745' }}></i>
              <div>
                <h6 className="mb-1 fw-bold text-white">Scambi gratuiti!</h6>
                <small className="mb-0">
                  Puoi effettuare scambi gratuitamente fino al {freeSwapEndDate.toLocaleDateString('it-IT')}.
                </small>
              </div>
            </div>
          )}

          {currentShow && (
            <div className="alert alert-info d-flex align-items-start gap-3 rounded-4 border-0 shadow-sm mb-4 bg-info bg-opacity-20 text-light">
              <i className="bi bi-tv fs-4 mt-1 text-brand-secondary"></i>
              <div>
                <h6 className="mb-1 brand-title">Show in corso: {currentShow.nome}</h6>
                <small className="mb-0">
                  Blocco attivo fino al{' '}
                  {currentShow.endTime.toLocaleString('it-IT', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </small>
              </div>
            </div>
          )}

          <div className="row g-3">
            <div className="col-12 col-lg-6">
              <section className="profile-panel-card h-100">
                <div className="profile-panel-head">
                  <div className="d-flex justify-content-between align-items-center edit-faction-market__header-row">
                    <div>
                      <h4 className="brand-title mb-1">Wrestler da Rimuovere</h4>
                      <small className="text-muted-brand">
                        Seleziona chi vuoi liberare dalla tua fazione.
                      </small>
                    </div>
                    <div className="badge bg-brand-secondary text-dark rounded-pill px-3 py-2 edit-faction-market__badge">
                      Rimasti: {Math.max(0, maxSwaps - (faction.swapsUsed || 0))}/{maxSwaps}
                    </div>
                  </div>
                </div>

                <div className="pt-0">
                  <div className="market-choice-card p-0 overflow-hidden h-100">
                    <div className="table-responsive">
                      <table className="table align-middle mb-0 table-dark edit-faction-market__table">
                        <thead>
                          <tr className="border-secondary">
                            <th className="border-0 bg-transparent">Wrestler</th>
                            <th className="border-0 bg-transparent">Ruolo</th>
                            <th className="text-end border-0 bg-transparent">Punti</th>
                            <th className="text-end border-0 bg-transparent">Costo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {members.map((member) => {
                            const isSelected = selectedToRemove.includes(member.id);
                            const statusMeta = getStatusMeta(member.status);

                            return (
                              <tr
                                key={member.id}
                                className={`border-secondary edit-faction-market__select-row ${isSelected ? 'is-selected' : ''}`}
                                onClick={() => onToggleRemove(member.id)}
                                onKeyDown={(event) =>
                                  handleSelectableRowKeyDown(event, () => onToggleRemove(member.id))
                                }
                                role="button"
                                tabIndex={0}
                                aria-pressed={isSelected}
                              >
                                <td className="border-0">
                                  <div className="d-flex align-items-center gap-3">
                                    {renderSelectionAvatar(member, isSelected)}
                                    <div className="d-flex flex-column">
                                      <strong className="fs-6 text-light">
                                        {member.name}
                                        {member.gender === 'Uomo' ? (
                                          <i className="bi bi-gender-male text-brand-primary-ultralight ms-1" title="Uomo"></i>
                                        ) : member.gender === 'Donna' ? (
                                          <i className="bi bi-gender-female text-brand-accent-light ms-1" title="Donna"></i>
                                        ) : (
                                          <i className="bi bi-gender-ambiguous text-secondary ms-1" title={member.gender || 'N/A'}></i>
                                        )}
                                        {member.isInjured && (
                                          <span className="badge bg-danger bg-opacity-25 text-danger ms-2">
                                            <i className="bi bi-bandaid-fill me-1"></i>
                                            Infortunato
                                          </span>
                                        )}
                                      </strong>
                                    </div>
                                  </div>
                                </td>
                                <td className="border-0">
                                  <span className={`badge ${statusMeta.className}`}>
                                    {statusMeta.label}
                                  </span>
                                </td>
                                <td className="text-end border-0">
                                  <span className="fw-bold text-light">
                                    {member.currentScore ?? member.score ?? 0}
                                  </span>
                                </td>
                                <td className="text-end border-0">
                                  <span className="text-money">
                                    {member.cost} <CreditIcon className="me-1" size={16} />
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="col-12 col-lg-6">
              <section className="profile-panel-card h-100">
                <div className="profile-panel-head edit-faction-market__panel-head">
                  <div className="edit-faction-market__header-stack">
                    <div className="edit-faction-market__hero-row">
                      <div className="edit-faction-market__title-block">
                        <h4 className="brand-title mb-0">Scegli Nuovi Wrestler</h4>
                        <small className="text-muted-brand d-block mt-1">
                          {selectedToRemove.length === 0
                            ? 'Seleziona prima chi togliere, poi scegli i sostituti.'
                            : `${selectedToAdd.length}/${selectedToRemove.length} sostituti scelti`}
                        </small>
                      </div>

                      <div className="edit-faction-market__meta-pills">
                        <span className="badge bg-brand-secondary text-dark rounded-pill px-3 py-2 edit-faction-market__badge">
                          Liberi: {allWrestlers.length}
                        </span>

                      </div>
                    </div>

                    <div className="market-choice-card edit-faction-market__budget-card edit-faction-market__budget-card--desktop">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted-brand">Crediti disponibili:</span>
                        <span className="fw-bold text-brand-secondary">
                          {faction.creditsLeft} <CreditIcon className="me-1" size={16} />
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted-brand">Crediti liberati:</span>
                        <span className="fw-bold text-brand-secondary">
                          +{selectedToRemoveCredits} <CreditIcon className="me-1" size={16} />
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted-brand">Crediti spesi:</span>
                        <span className="fw-bold text-brand-accent">
                          -{selectedToAddCost} <CreditIcon className="me-1" size={16} />
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted-brand">Budget rimanente:</span>
                        <span className={`fw-bold ${remainingBudget >= 0 ? 'text-brand-secondary' : 'text-brand-accent'}`}>
                          {remainingBudget} <CreditIcon className="me-1" size={16} />
                        </span>
                      </div>
                    </div>

                    {hasGenderRequirements && selectedToRemove.length > 0 && (
                      <div
                        className={`alert bg-transparent ${genderSelectionState.impossible ? 'alert-danger' : 'alert-info'} rounded-4 shadow-sm mb-0 ${
                          genderSelectionState.impossible ? 'bg-danger bg-opacity-20 text-light' : 'bg-info bg-opacity-20 text-light'
                        }`}
                        style={{ border: '1px solid var(--brand-primary-ultralight)' }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          <i
                            className={`bi ${genderSelectionState.impossible ? 'bi-exclamation-triangle-fill' : 'bi-gender-ambiguous'} fs-5 mt-1 ${
                              genderSelectionState.impossible ? 'text-brand-accent' : 'text-brand-secondary'
                            }`}
                          ></i>
                          <div>
                            <div className="fw-bold text-white mb-1">Vincoli minimi del roster</div>
                            <div className="small mb-0">
                              {genderSelectionState.message || 'La selezione attuale rispetta gia i minimi uomo/donna richiesti dal roster.'}
                            </div>
                            <small className="d-block mt-2 text-white-50">
                              Minimi richiesti:
                              {minMaleWrestlers > 0 ? ` ${getGenderRequirementText(minMaleWrestlers, 'Uomo')}` : ''}
                              {minMaleWrestlers > 0 && minFemaleWrestlers > 0 ? ' e' : ''}
                              {minFemaleWrestlers > 0 ? ` ${getGenderRequirementText(minFemaleWrestlers, 'Donna')}` : ''}
                            </small>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="edit-faction-market__controls-grid">
                      <div className="market-choice-card edit-faction-market__control-card">
                        <div className="edit-faction-market__control-head">
                          <div>
                            <div className="fw-bold text-white">Filtri</div>
                            <small className="text-muted-brand">Trova piu velocemente i sostituti giusti.</small>
                          </div>
                          {hasActiveFilters && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-brand-secondary"
                              onClick={() => {
                                setWrestlerSearch('');
                                setBrandFilter('');
                                setGenderFilter('');
                              }}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Reset
                            </button>
                          )}
                        </div>

                        <div className="edit-faction-market__filters-grid">
                          <div className="input-group edit-faction-market__search-field">
                            <span className="input-group-text bg-transparent border-secondary">
                              <i className="bi bi-search text-muted-brand"></i>
                            </span>
                            <input
                              type="text"
                              className="form-control market-dark-select"
                              placeholder="Cerca wrestler..."
                              value={wrestlerSearch}
                              onChange={(event) => setWrestlerSearch(event.target.value)}
                            />
                          </div>

                          <select
                            className="form-select form-select-sm market-dark-select"
                            value={brandFilter}
                            onChange={(event) => setBrandFilter(event.target.value)}
                          >
                            <option value="">Tutti brand</option>
                            {availableBrands.map((brand) => (
                              <option key={brand} value={brand}>
                                {brand}
                              </option>
                            ))}
                          </select>

                          <select
                            className="form-select form-select-sm market-dark-select"
                            value={genderFilter}
                            onChange={(event) => setGenderFilter(event.target.value)}
                          >
                            <option value="">Tutti sessi</option>
                            {availableGenders.map((gender) => (
                              <option key={gender} value={gender}>
                                {gender}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="market-choice-card edit-faction-market__control-card">
                        <div className="edit-faction-market__control-head">
                          <div>
                            <div className="fw-bold text-white">Ordinamento</div>
                            <small className="text-muted-brand">Organizza la lista per nome, punti o prezzo.</small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center edit-faction-market__sort-row">
                          <div className="btn-group btn-group-sm" role="group">
                            {['name', 'score', 'price'].map((field) => (
                              <button
                                key={field}
                                type="button"
                                className={`btn ${sortField === field ? 'btn-brand-secondary' : 'btn-outline-brand-secondary'}`}
                                onClick={() => setSortField(field)}
                              >
                                {field === 'name' ? 'Nome' : field === 'score' ? 'Punti' : 'Prezzo'}
                                {sortField === field && (
                                  <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                                )}
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-brand-secondary btn-sm"
                            onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                          >
                            <i className={`bi bi-arrow-${sortDirection === 'asc' ? 'down' : 'up'}`}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-0">
                  <div className="market-choice-card p-0 overflow-hidden h-100">
                    <div className="table-responsive" style={{ maxHeight: '400px' }}>
                      <table className="table table-dark align-middle mb-0 edit-faction-market__table">
                        <thead className="bg-brand-dark bg-opacity-50 sticky-top">
                          <tr className="border-secondary">
                            <th className="border-0 bg-transparent">Wrestler</th>
                            <th className="text-end border-0 bg-transparent">Punti</th>
                            <th className="text-end border-0 bg-transparent">Prezzo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredWrestlers.length === 0 ? (
                            <tr>
                              <td colSpan="3" className="text-center py-4">
                                <i className="bi bi-search fs-3 text-muted-brand d-block mb-2"></i>
                                <p className="mt-2 mb-0 text-muted-brand">
                                  {allWrestlers.length === 0
                                    ? 'Non ci sono wrestler liberi compatibili con questo roster.'
                                    : 'Nessun wrestler trovato con i filtri attuali.'}
                                </p>
                              </td>
                            </tr>
                          ) : (
                            filteredWrestlers.map((wrestler) => {
                              const isSelected = selectedToAdd.includes(wrestler.id);
                              const isMaxReached = selectedToAdd.length >= selectedToRemove.length && !isSelected;
                              const canAfford = wrestler.price <= remainingBudget;
                              const candidateGenderState = getCandidateGenderState(wrestler.id);
                              const violatesGenderRequirements = !isSelected && candidateGenderState.impossible;
                              const isSelectable = !isMaxReached && canAfford && !violatesGenderRequirements;
                              const isBlocked = !isSelected && !isSelectable;

                              return (
                                <tr
                                  key={wrestler.id}
                                  className={`border-secondary edit-faction-market__select-row ${isSelected ? 'is-selected' : ''} ${isBlocked ? 'is-disabled wrestler-disabled opacity-50' : ''}`}
                                  onClick={() => (isSelected || isSelectable) && onToggleAdd(wrestler.id)}
                                  onKeyDown={(event) =>
                                    handleSelectableRowKeyDown(
                                      event,
                                      () => onToggleAdd(wrestler.id),
                                      !isSelected && !isSelectable
                                    )
                                  }
                                  role="button"
                                  tabIndex={isBlocked ? -1 : 0}
                                  aria-pressed={isSelected}
                                  aria-disabled={isBlocked}
                                >
                                  <td className="border-0">
                                    <div className="d-flex align-items-center gap-3">
                                      {renderSelectionAvatar(wrestler, isSelected)}
                                      <div className="d-flex flex-column">
                                        <strong className="fs-6 text-light">
                                          {wrestler.name}
                                          {wrestler.gender === 'Uomo' ? (
                                            <i className="bi bi-gender-male text-brand-primary-ultralight ms-1" title="Uomo"></i>
                                          ) : wrestler.gender === 'Donna' ? (
                                            <i className="bi bi-gender-female text-brand-accent-light ms-1" title="Donna"></i>
                                          ) : (
                                            <i className="bi bi-gender-ambiguous text-secondary ms-1" title={wrestler.gender || 'N/A'}></i>
                                          )}
                                          {wrestler.isInjured && (
                                            <span className="badge bg-danger bg-opacity-25 text-light ms-2">
                                              <i className="bi bi-bandaid-fill me-1"></i>
                                              Infortunato
                                            </span>
                                          )}
                                        </strong>
                                        <div className="d-flex gap-1 mt-1 flex-wrap">
                                          <BrandBadge brand={wrestler.brand} />
                                          {isBlocked && (
                                            <span className="badge bg-brand-accent bg-opacity-75 text-light px-2 py-1">
                                              {isMaxReached
                                                ? 'Max raggiunto'
                                                : !canAfford
                                                  ? 'Fuori budget'
                                                  : 'Minimi sesso'}
                                            </span>
                                          )}
                                        </div>
                                        {violatesGenderRequirements && candidateGenderState.message && (
                                          <small className="text-brand-accent mt-1">
                                            {candidateGenderState.message}
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-end border-0">
                                    <span className="text-brand-secondary fw-bold small">{wrestler.score}</span>
                                  </td>
                                  <td className="text-end border-0">
                                    <span className={`${canAfford ? 'text-money' : 'text-accent'} px-2 py-1`}>
                                      {wrestler.price} <CreditIcon className="me-1" size={16} />
                                      {isBlocked && !canAfford && <i className="bi bi-lock ms-1"></i>}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          {selectedToRemove.length > 0 && (
            <section className="profile-panel-card mt-4">
              <div className="profile-panel-head d-flex justify-content-between align-items-center edit-faction-market__header-row">
                <h4 className="mb-0 brand-title">
                  <i className="bi bi-arrow-left-right me-2"></i>Riepilogo Scambio
                </h4>
                <span className="badge bg-brand-secondary text-dark rounded-pill px-3 py-2 edit-faction-market__badge">
                  {selectedToRemove.length} scambi
                </span>
              </div>
              <div className="p-3 pt-0">
                <div className="market-choice-card edit-faction-market__summary-card">
                  <div className="row">
                    <div className="col-12 col-md-6">
                      <h6 className="text-center mb-3 text-brand-accent">
                        <i className="bi bi-box-arrow-right me-2"></i>In uscita
                      </h6>
                      {selectedToRemove.map((id) => {
                        const member = members.find((item) => item.id === id);
                        return (
                          member && (
                            <div key={id} className="profile-inline-empty d-flex justify-content-between align-items-center mb-2">
                              <span className="fw-medium text-light">{member.name}</span>
                              <span className="text-brand-secondary fw-bold">
                                +{member.cost} <CreditIcon className="me-1" size={16} />
                              </span>
                            </div>
                          )
                        );
                      })}
                      <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-secondary">
                        <div className="fw-medium text-light">Totale in entrata</div>
                        <div className="text-brand-secondary fw-bold">
                          +{priceDetails.totalOut} <CreditIcon className="me-1" size={16} />
                        </div>
                      </div>
                    </div>

                    <div className="col-12 col-md-6 mt-3 mt-md-0">
                      <h6 className="text-center mb-3 text-brand-secondary">
                        <i className="bi bi-box-arrow-in-left me-2"></i>In entrata
                      </h6>
                      {selectedToAdd.map((id) => {
                        const wrestler = allWrestlersById[id];
                        return (
                          wrestler && (
                            <div key={id} className="profile-inline-empty d-flex justify-content-between align-items-center mb-2">
                              <span className="fw-medium text-light">{wrestler.name}</span>
                              <span className="text-brand-accent fw-bold">
                                -{wrestler.price} <CreditIcon className="me-1" size={16} />
                              </span>
                            </div>
                          )
                        );
                      })}
                      <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-secondary">
                        <div className="fw-medium text-light">Totale in uscita</div>
                        <div className="text-brand-accent fw-bold">
                          -{priceDetails.totalIn} <CreditIcon className="me-1" size={16} />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-top border-secondary">
                    <div
                      className={`d-flex justify-content-between align-items-center ${priceDetails.newCredits < 0 ? 'text-brand-accent' : 'text-brand-secondary'
                        }`}
                    >
                      <div>
                        <i className="bi bi-wallet2 me-2"></i>
                        <span className="fw-medium text-light">Crediti finali</span>
                      </div>
                      <div className="fw-bold fs-5">
                        {priceDetails.newCredits} <CreditIcon className="me-1" size={16} />
                        {priceDetails.newCredits < 0 && (
                          <span className="badge bg-brand-accent ms-2">
                            <i className="bi bi-exclamation-triangle me-1"></i> insufficienti
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="d-grid mt-4">
                    <button
                      className="btn btn-brand-secondary rounded-pill px-4"
                      onClick={onSaveMembers}
                      disabled={
                        selectedToRemove.length !== selectedToAdd.length ||
                        selectedToRemove.length === 0 ||
                        priceDetails.newCredits < 0 ||
                        genderSelectionState.impossible ||
                        (swapBlockData && !swapBlockData.enabled) ||
                        autoBlocked ||
                        isSaving
                      }
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Salvataggio...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Conferma Scambio
                          {!isWithinFirstDay && ` (${swapCount + selectedToRemove.length}/${maxSwaps})`}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {error && (
        <div className="alert alert-danger mt-4 bg-danger bg-opacity-20 text-light border-0" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
        </div>
      )}
    </div>
  );
}

export default EditFactionFormWrestler;
